import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@resource-kit/ui';
import type { AnalyticsMetrics } from './analyticsTypes';
import { formatCount, formatMoney } from './analyticsFormat';
import { formatDateRange, previousPeriodRange, previousYearRange } from './reportLabel';
import { SectionHeading } from './SectionHeading';

function normalizedPercent(value: number): number {
  return Math.abs(value) < 0.0005 ? 0 : value;
}

export function formatAnalyticsPercent(value: number, locale: string | undefined): string {
  const normalized = normalizedPercent(value);
  const formatted = new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(normalized);
  return normalized > 0 ? `+${formatted}` : formatted;
}

function Delta({
  value,
  label,
  locale,
}: {
  value: number | null;
  label: string;
  locale: string | undefined;
}) {
  const { t } = useTranslation();
  const direction = value === null ? 0 : Math.sign(normalizedPercent(value));
  const Icon = direction > 0 ? ArrowUpRight : direction < 0 ? ArrowDownRight : Minus;

  return (
    <span className="flex items-center gap-1 text-xs">
      <Icon
        aria-hidden
        className={cn(
          'size-3.5 shrink-0',
          direction > 0 && 'text-success',
          direction < 0 && 'text-destructive',
          direction === 0 && 'text-muted-foreground',
        )}
      />
      {value === null ? (
        <span className="text-muted-foreground">{t('analytics.performance.noComparison')}</span>
      ) : (
        <span
          className={cn(
            'font-medium tabular-nums',
            direction > 0 && 'text-success',
            direction < 0 && 'text-destructive',
            direction === 0 && 'text-muted-foreground',
          )}
        >
          {formatAnalyticsPercent(value, locale)}
        </span>
      )}
      <span className="text-muted-foreground">{label}</span>
    </span>
  );
}

interface Kpi {
  key: string;
  label: string;
  value: string;
  periodChange?: number | null;
  yearChange?: number | null;
  note?: string;
}

/** The report period's own results ("本期表现") with their comparisons. */
export function ExecutiveSummary({
  metrics,
  periodStart,
  periodEnd,
}: {
  metrics: AnalyticsMetrics;
  periodStart: string;
  periodEnd: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const previous = previousPeriodRange(periodStart, periodEnd);
  const previousYear = previousYearRange(periodStart, periodEnd);
  const { orders, revenue, ordering_customers: customers } = metrics.growth;
  const kpis: Kpi[] = [
    {
      key: 'orders',
      label: t('analytics.metrics.orders'),
      value: formatCount(orders.current, locale),
      periodChange: orders.period_over_period_change,
      yearChange: orders.year_over_year_change,
    },
    {
      key: 'revenue',
      label: t('analytics.metrics.revenue'),
      value: formatMoney(revenue.current, locale),
      periodChange: revenue.period_over_period_change,
      yearChange: revenue.year_over_year_change,
    },
    {
      key: 'customers',
      label: t('analytics.metrics.orderingCustomers'),
      value: formatCount(customers.current, locale),
      periodChange: customers.period_over_period_change,
      yearChange: customers.year_over_year_change,
    },
    {
      key: 'items',
      label: t('analytics.metrics.averageItemsLabel'),
      value: formatCount(metrics.leverage.average_items_per_order, locale),
      note: t('analytics.performance.itemsSold', {
        count: metrics.leverage.item_count,
        n: formatCount(metrics.leverage.item_count, locale),
      }),
    },
  ];

  return (
    <section className="flex flex-col gap-5" aria-labelledby="analytics-performance-title">
      <SectionHeading
        id="analytics-performance-title"
        title={t('analytics.performance.title')}
        description={
          <span className="flex flex-wrap gap-x-3 gap-y-0.5">
            <span>
              {t('analytics.performance.periodWindow', {
                range: formatDateRange(previous.start, previous.end, locale),
              })}
            </span>
            <span>
              {t('analytics.performance.yearWindow', {
                range: formatDateRange(previousYear.start, previousYear.end, locale, {
                  withYear: true,
                }),
              })}
            </span>
          </span>
        }
      />
      <dl className="grid grid-cols-2 gap-x-4 gap-y-6 lg:grid-cols-4">
        {kpis.map((kpi) => (
          <div key={kpi.key} className="flex min-w-0 flex-col gap-1">
            <dt className="text-xs font-medium text-muted-foreground">{kpi.label}</dt>
            <dd className="truncate text-2xl font-semibold tracking-tight tabular-nums sm:text-3xl">
              {kpi.value}
            </dd>
            {kpi.periodChange !== undefined && (
              <dd>
                <Delta
                  value={kpi.periodChange}
                  label={t('analytics.performance.periodChange')}
                  locale={locale}
                />
              </dd>
            )}
            {kpi.yearChange !== undefined && (
              <dd>
                <Delta
                  value={kpi.yearChange}
                  label={t('analytics.performance.yearChange')}
                  locale={locale}
                />
              </dd>
            )}
            {kpi.note && <dd className="text-xs text-muted-foreground">{kpi.note}</dd>}
          </div>
        ))}
      </dl>
    </section>
  );
}
